import React, { useState, useMemo } from 'react';
import { ArrowRight, X, Circle, Calendar, CalendarClock, Undo2, Clock, Loader2, BarChart3, AlertTriangle, DollarSign, ShoppingCart, Info, Gift, Check, Pause } from 'lucide-react';
import { PatientProductHistoryModal } from './PatientProductHistoryModal';
import { DatePicker } from '@/components/ui/DatePicker';

// ── Types ────────────────────────────────────────────────────────────────────

export interface ProductItem {
  productID: number | null;
  productName: string;
  productTypeID: number | null;
  productTypeName: string;
  equivalentProductTypes: unknown[];
  productCategoryID: unknown[];
  paymentID: number | null;
  membershipID: number | null;
  packageID: number | null;
  accountID: number | null;
  facilityID: unknown[];
  paymentMaterialID: number | null;
  membershipProductID: number | null;
  packageProductID: number | null;
  scheduledAppointmentProductsID: number | null;
  auditRecordID: string | null;
  scheduledAppointmentsID: number | null;
  quantity: number;
  addedDate: string | null;
  usedDate: string | null;
  expireDate: string | null;
  deleteDate: string | null;
  scheduledAppointmentStartTime?: string | null;
  appointmentProductAddonsID?: string | null;
  appointmentTypeName?: string | null;
  type?: string | null;
  actionUserID?: number | string | null;
  actionUserName?: string | null;
  holdReason?: 'frozen' | 'declined' | 'cancelled' | null;
  effectiveExpireDate?: string | null;
  originalExpireDate?: string | null;
}

export interface ProductGroup {
  productTypeID: number | null;
  productTypeName: string;
  productID?: number | null;
  productName?: string;
  equivalentProductTypes: unknown[];
  productCategoryID: unknown[];
  availableQuantity: number;
  upcomingQuantity: number;
  appointmentsQuantity: number;
  usedQuantity: number;
  expireQuantity: number;
  deleteQuantity: number;
  available: ProductItem[];
  upcoming: ProductItem[];
  appointments: ProductItem[];
  comped: ProductItem[];
  compedQuantity: number;
  purchase: ProductItem[];
  unavailable: ProductItem[];
  onHold: ProductItem[];
  onHoldQuantity: number;
  curRate?: number;
}

export interface ProductTypesResponse {
  data: {
    'product-types': Record<string, ProductGroup>;
    'product-others': Record<string, ProductGroup>;
    isMember?: boolean;
  };
}

export type ActionHandler = (
  action: 'consume' | 'delete' | 'return',
  item: ProductItem,
  quantity?: number,
  skipRefetch?: boolean,
) => Promise<void>;

export type BatchConsumeHandler = (items: ProductItem[]) => Promise<void>;

export type UpdateExpireDateHandler = (
  item: ProductItem,
  newExpireDate: string,
  quantity?: number,
) => Promise<void>;

// ── Helpers ──────────────────────────────────────────────────────────────────

export function toArray<T>(value: unknown): T[] {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return Object.values(value) as T[];
  return [];
}

export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'America/Los_Angeles',
  });
}

export function getPaymentSource(item: ProductItem) {
  if (item.paymentMaterialID && item.paymentMaterialID !== 0) return '(PAYMENT)';
  if (item.membershipProductID && item.membershipProductID !== 0) return '(MEMBERSHIP)';
  if (item.packageProductID && item.packageProductID !== 0) return '(PACKAGE)';
  return '';
}

export function resolveTypeAndId(item: ProductItem): { type: string; productId: number } | null {
  if (item.paymentMaterialID && item.paymentMaterialID !== 0)
    return { type: 'payment', productId: item.paymentMaterialID };
  if (item.membershipProductID && item.membershipProductID !== 0)
    return { type: 'membership', productId: item.membershipProductID };
  if (item.packageProductID && item.packageProductID !== 0)
    return { type: 'package', productId: item.packageProductID };
  return null;
}

function isUpcomingProduct(item: ProductItem): boolean {
  return !resolveTypeAndId(item);
}

function isExpiredAndNotDeleted(item: ProductItem): boolean {
  const hasExpire = !!item.expireDate && item.expireDate !== '0000-00-00 00:00:00';
  const hasDelete = !!item.deleteDate && item.deleteDate !== '0000-00-00 00:00:00';
  if (!hasExpire || hasDelete) return false;
  return new Date() > new Date(item.expireDate!);
}

// ── Shared icon button ───────────────────────────────────────────────────────

const ICON_BTN =
  'inline-flex items-center justify-center rounded-full transition-colors disabled:opacity-50';
const ICON_SM = 'h-6 w-6'; // appointment rows
const ICON_MD = 'h-7 w-7'; // available / used / expired rows

function IconBtn({ icon, title, onClick, disabled, loading, className, size = 'sm' }: {
  icon: React.ReactNode;
  title: string;
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
  loading?: boolean;
  className: string;
  size?: 'sm' | 'md';
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled || loading}
      onClick={(e) => { e.stopPropagation(); onClick(e); }}
      className={`${ICON_BTN} ${size === 'sm' ? ICON_SM : ICON_MD} ${className}`}
    >
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : icon}
    </button>
  );
}

function QtySelect({ max, value, onChange }: {
  max: number;
  value: number;
  onChange: (v: number) => void;
}) {
  if (max <= 1) return null;
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="h-6 rounded border border-white/30 bg-white/20 px-0.5 text-xs text-white"
    >
      {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
        <option key={n} value={n} className="bg-gray-700 text-white">{n}</option>
      ))}
    </select>
  );
}

// ── Action column width ──────────────────────────────────────────────────────
const ACTION_COL = 'flex w-24 shrink-0 items-center justify-end gap-1';

// ── Sub-components ───────────────────────────────────────────────────────────

export function CountBadge({ count, icon, title, bg, text = 'text-white', large }: {
  count: number;
  icon: React.ReactNode;
  title: string;
  bg: string;
  text?: string;
  large?: boolean;
}) {
  return (
    <span
      title={`${title}: ${count}`}
      className={`inline-flex items-center justify-center gap-0.5 rounded font-semibold tabular-nums ${bg} ${text} ${
        large ? 'h-7 w-14 text-xs' : 'h-6 w-10 text-[10px]'
      }`}
    >
      {icon}
      {count}
    </span>
  );
}

function AutoDisburseNotice() {
  const [expanded, setExpanded] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setExpanded((v) => !v)}
      className="mb-2 flex w-full items-start gap-1.5 rounded border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-left text-xs text-blue-800 hover:bg-blue-100 transition-colors"
    >
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-500" />
      <span className="flex-1">
        Best practice: attach products to appointments for auto-disbursement rather than disbursing manually.
        {expanded && (
          <ul className="mt-1 list-disc pl-4 text-blue-700 leading-relaxed space-y-0.5">
            <li>Products attached to appointments will auto-disburse at checkout unless comped.</li>
            <li>Manually disbursing products that are not attached to an appointment is not recommended.</li>
            <li>If products are added after checkout, they will remain reserved and not available for use elsewhere, but will require manual disbursement from this list or from the appointment details.</li>
            <li className="flex items-center gap-1.5">The <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-orange-600"><Calendar className="h-2.5 w-2.5 text-white" /></span> button will display the appointment details.</li>
          </ul>
        )}
      </span>
      <span className="mt-0.5 shrink-0 text-xs font-medium text-blue-600">
        {expanded ? 'Less' : 'More info…'}
      </span>
    </button>
  );
}

export function SubListToggle({ title, count, children, defaultOpen = false, countColor = 'text-gray-500' }: {
  title: string;
  count: number;
  children: React.ReactNode;
  defaultOpen?: boolean;
  countColor?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (count === 0) return null;
  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs font-semibold text-gray-600 hover:bg-gray-100"
      >
        <span className={`inline-block transition-transform ${open ? 'rotate-90' : ''}`}>&#9654;</span>
        <span className={`inline-block min-w-[1.25rem] text-right ${countColor}`}>{count}</span>
        {title}
      </button>
      {open && <div className="ml-4 mt-1 space-y-1">{children}</div>}
    </div>
  );
}

function ViewAppointmentButton({ appointmentId, onOpenAppointment, size = 'sm' }: {
  appointmentId: number;
  onOpenAppointment: (id: number) => void;
  size?: 'sm' | 'md';
}) {
  return (
    <IconBtn
      icon={<Calendar className="h-3.5 w-3.5" />}
      title="View Appointment"
      onClick={() => onOpenAppointment(appointmentId)}
      className="bg-orange-600 text-white hover:bg-orange-700 hover:ring-2 hover:ring-orange-300"
      size={size}
    />
  );
}

export function AppointmentRow({ item, onAction, onOpenAppointment }: {
  item: ProductItem;
  onAction: ActionHandler;
  onOpenAppointment?: (id: number) => void;
}) {
  const source = getPaymentSource(item);
  const isPast = item.scheduledAppointmentStartTime
    ? new Date() > new Date(item.scheduledAppointmentStartTime)
    : false;
  const upcoming = isUpcomingProduct(item);

  return (
    <div className={`flex items-center gap-3 rounded px-2 py-1.5 text-xs text-white ${isPast ? 'bg-[#8B0000]' : 'bg-[#357bb8]'}`}>
      <span className="w-[30%] max-w-[30%] truncate font-medium">
        {item.productName} (x{item.quantity}) {source}
        {upcoming && <span className="ml-1 opacity-70">(UPCOMING)</span>}
      </span>
      <span className={isPast ? 'flex-1 opacity-80' : 'flex-1 opacity-80'}>
        Appointment on: {formatDate(item.scheduledAppointmentStartTime)}
      </span>
      <span className="flex items-center gap-1">
        {onOpenAppointment && item.scheduledAppointmentsID && (
          <ViewAppointmentButton
            appointmentId={item.scheduledAppointmentsID}
            onOpenAppointment={onOpenAppointment}
          />
        )}
        {!upcoming ? (
          <UseButton item={item} onAction={onAction} />
        ) : (
          <span className="rounded bg-white/20 px-2 py-0.5 text-xs font-medium text-white" title="Upcoming — not yet available">
            Upcoming
          </span>
        )}
      </span>
    </div>
  );
}

interface ApptGroup {
  apptId: number | null;
  date: string | null;
  apptTypeName: string | null;
  items: ProductItem[];
  totalQty: number;
  isPast: boolean;
  allUpcoming: boolean;
}

function groupByAppointment(items: ProductItem[]): ApptGroup[] {
  const map = new Map<string, ApptGroup>();
  for (const item of items) {
    const upcoming = isUpcomingProduct(item);
    const key = `${item.scheduledAppointmentsID ?? 'none'}:${upcoming ? 'upcoming' : 'existing'}`;
    const existing = map.get(key);
    if (existing) {
      existing.items.push(item);
      existing.totalQty += item.quantity;
    } else {
      const isPast = item.scheduledAppointmentStartTime
        ? new Date() > new Date(item.scheduledAppointmentStartTime)
        : false;
      map.set(key, {
        apptId: item.scheduledAppointmentsID,
        date: item.scheduledAppointmentStartTime ?? null,
        apptTypeName: item.appointmentTypeName ?? null,
        items: [item],
        totalQty: item.quantity,
        isPast,
        allUpcoming: upcoming,
      });
    }
  }
  return Array.from(map.values());
}

export function AppointmentGroupRow({ group, onBatchConsume, onOpenAppointment, colorClass, isComped, readOnly }: {
  group: ApptGroup;
  onBatchConsume?: BatchConsumeHandler;
  onOpenAppointment?: (id: number) => void;
  colorClass?: string;
  isComped?: boolean;
  readOnly?: boolean;
}) {
  const [busy, setBusy] = useState(false);

  async function handleUseAll() {
    if (!onBatchConsume) return;
    setBusy(true);
    try {
      await onBatchConsume(group.items);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`flex items-center gap-3 rounded px-2 py-1.5 text-xs text-white ${colorClass ?? (group.isPast ? 'bg-[#8B0000]' : 'bg-[#357bb8]')}`}>
      <span className="font-medium">
        (x{group.totalQty})
      </span>
      {group.apptTypeName && (
        <span className="truncate font-medium">
          {group.apptTypeName}
        </span>
      )}
      <span className="opacity-80">
        {group.date ? formatDate(group.date) : 'Unscheduled'}
      </span>
      <span className="flex-1" />
      <span className={ACTION_COL}>
        {onOpenAppointment && group.apptId && (
          <ViewAppointmentButton appointmentId={group.apptId} onOpenAppointment={onOpenAppointment} />
        )}
        {isComped ? (
          !readOnly && (
            <IconBtn
              icon={<Gift className="h-3.5 w-3.5" />}
              title="Comped"
              onClick={() => {}}
              className="bg-white/25 text-white cursor-default"
            />
          )
        ) : group.allUpcoming ? (
          <IconBtn
            icon={<Clock className="h-3.5 w-3.5" />}
            title="Upcoming — not yet available"
            onClick={() => {}}
            className="bg-white/25 text-white cursor-default"
          />
        ) : !readOnly && onBatchConsume ? (
          <IconBtn
            icon={<ArrowRight className="h-3.5 w-3.5" />}
            title="Disburse all"
            onClick={handleUseAll}
            disabled={busy}
            loading={busy}
            className="bg-green-600 text-white hover:bg-green-700 hover:ring-2 hover:ring-green-300"
          />
        ) : null}
      </span>
    </div>
  );
}

export function UseButton({ item, onAction, size = 'md', qty: controlledQty, onQtyReset }: {
  item: ProductItem;
  onAction: ActionHandler;
  size?: 'sm' | 'md';
  /** When supplied the caller owns the quantity selector; the button renders none of its own. */
  qty?: number;
  onQtyReset?: () => void;
}) {
  const [localQty, setLocalQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const qty = controlledQty ?? localQty;

  async function handleUse() {
    setBusy(true);
    try {
      await onAction('consume', item, qty);
    } finally {
      setBusy(false);
      setLocalQty(1);
      onQtyReset?.();
    }
  }

  return (
    <>
      {controlledQty === undefined && (
        <QtySelect max={item.quantity} value={localQty} onChange={setLocalQty} />
      )}
      <IconBtn
        icon={<ArrowRight className="h-3.5 w-3.5" />}
        title="Use"
        onClick={handleUse}
        disabled={busy}
        loading={busy}
        className="bg-green-600 text-white hover:bg-green-700 hover:ring-2 hover:ring-green-300"
        size={size}
      />
    </>
  );
}

export function DeleteButton({ item, onAction, size = 'md', qty: controlledQty, onQtyReset }: {
  item: ProductItem;
  onAction: ActionHandler;
  size?: 'sm' | 'md';
  /** When supplied the caller owns the quantity selector; the button renders none of its own. */
  qty?: number;
  onQtyReset?: () => void;
}) {
  const [localQty, setLocalQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const qty = controlledQty ?? localQty;

  async function handleDelete() {
    const label = qty < item.quantity
      ? `Delete ${qty} of ${item.quantity} "${item.productName}"?`
      : `Delete "${item.productName}" (x${item.quantity})?`;
    if (!window.confirm(label)) return;
    setBusy(true);
    try {
      await onAction('delete', item, qty);
    } finally {
      setBusy(false);
      setLocalQty(1);
      onQtyReset?.();
    }
  }

  return (
    <>
      {controlledQty === undefined && (
        <QtySelect max={item.quantity} value={localQty} onChange={setLocalQty} />
      )}
      <IconBtn
        icon={<X className={size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'} />}
        title="Delete"
        onClick={handleDelete}
        disabled={busy}
        loading={busy}
        className="bg-red-600/80 text-white hover:bg-red-600 hover:ring-2 hover:ring-red-300"
        size={size}
      />
    </>
  );
}

export function ReturnButton({ item, onAction, size = 'md' }: {
  item: ProductItem;
  onAction: ActionHandler;
  size?: 'sm' | 'md';
}) {
  const [busy, setBusy] = useState(false);

  async function handleReturn() {
    if (!window.confirm(`Return "${item.productName}" (x${item.quantity})?`)) return;
    setBusy(true);
    try { await onAction('return', item); } finally { setBusy(false); }
  }

  return (
    <IconBtn
      icon={<Undo2 className="h-3.5 w-3.5" />}
      title="Return"
      onClick={handleReturn}
      disabled={busy}
      loading={busy}
      className="bg-amber-600 text-white hover:bg-amber-700 hover:ring-2 hover:ring-amber-300"
      size={size}
    />
  );
}

export function AvailableRow({ item, onAction, readOnly, onUpdateExpireDate, canEditExpireDate, canDelete }: {
  item: ProductItem;
  onAction: ActionHandler;
  readOnly?: boolean;
  onUpdateExpireDate?: UpdateExpireDateHandler;
  canEditExpireDate?: boolean;
  canDelete?: boolean;
}) {
  const source = getPaymentSource(item);
  const hasExpire = !!item.expireDate && item.expireDate !== '0000-00-00 00:00:00';
  const showEditExpire = hasExpire && canEditExpireDate && !!onUpdateExpireDate;

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [qty, setQty] = useState(item.quantity);
  // Shared by Use and Delete so the row shows one quantity selector, not two.
  const [actionQty, setActionQty] = useState(1);
  const todayStr = new Date().toISOString().slice(0, 10);
  const minDate = todayStr;
  const itemExpireStr = item.expireDate?.slice(0, 10) ?? '';
  const defaultDate = itemExpireStr >= todayStr ? itemExpireStr : todayStr;
  const [newDate, setNewDate] = useState(defaultDate);

  const handleSave = async () => {
    if (!onUpdateExpireDate || !newDate || newDate < todayStr) return;
    setSaving(true);
    try {
      await onUpdateExpireDate(item, newDate, qty);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const hasEffectiveExpire = !!item.effectiveExpireDate && item.effectiveExpireDate !== item.expireDate;
  const displayExpireDate = hasEffectiveExpire ? item.effectiveExpireDate : item.expireDate;

  return (
    <div>
      <div className="flex items-center gap-3 rounded bg-[#2f4d7e] px-2 py-1.5 text-xs text-white">
        <span className="w-[30%] max-w-[30%] truncate font-medium">
          {item.productName} (x{item.quantity}) {source}
        </span>
        <span className="flex-1 opacity-80">
          {item.addedDate && <>Added: {formatDate(item.addedDate)}</>}
          {item.actionUserName?.trim() && <> by {item.actionUserName.trim()}</>}
          {displayExpireDate && (
            <>
              {' — '}
              {hasEffectiveExpire ? (
                <span title={`Actual expiration: ${formatDate(item.expireDate!)}. Adjusted due to upcoming membership hold.`} className="border-b border-dashed border-white/50">
                  Expires: {formatDate(displayExpireDate)}
                </span>
              ) : (
                <>Expires: {formatDate(displayExpireDate)}</>
              )}
            </>
          )}
        </span>
        {!readOnly && (
          <span className={showEditExpire ? 'flex w-32 shrink-0 items-center justify-end gap-1' : ACTION_COL}>
            {showEditExpire && (
              <IconBtn
                icon={<CalendarClock className="h-3 w-3" />}
                title="Edit expiration date"
                onClick={() => setEditing(!editing)}
                className="bg-white/20 text-white hover:bg-white/30"
                size="md"
              />
            )}
            <QtySelect max={item.quantity} value={actionQty} onChange={setActionQty} />
            <UseButton item={item} onAction={onAction} qty={actionQty} onQtyReset={() => setActionQty(1)} />
            {canDelete && (
              <DeleteButton item={item} onAction={onAction} qty={actionQty} onQtyReset={() => setActionQty(1)} />
            )}
          </span>
        )}
      </div>
      {editing && (
        <div className="flex items-center gap-2 rounded-b border border-t-0 border-gray-200 bg-gray-50 px-3 py-2">
          {item.quantity > 1 && (
            <>
              <label className="text-xs font-medium text-gray-600">Qty:</label>
              <select
                value={qty}
                onChange={(e) => setQty(Number(e.target.value))}
                className="h-6 rounded border border-gray-300 bg-white px-1 text-xs text-gray-700"
              >
                {Array.from({ length: item.quantity }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <span className="text-xs text-gray-400">of {item.quantity}</span>
            </>
          )}
          <label className="text-xs font-medium text-gray-600">New expiration:</label>
          <DatePicker
            size="sm"
            min={minDate}
            value={newDate}
            onChange={setNewDate}
            ariaLabel="New expiration date"
            wrapperClassName="w-40"
          />
          <button
            type="button"
            disabled={saving || !newDate || newDate < todayStr}
            onClick={handleSave}
            className="inline-flex items-center gap-1 rounded bg-blue-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-600 disabled:opacity-50 transition-colors"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            Save
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <X className="h-3 w-3" />
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

export function UpcomingRow({ item }: { item: ProductItem }) {
  return (
    <div className="flex items-center gap-3 rounded bg-[#c97a28] px-2 py-1.5 text-xs text-white">
      <span className="w-[30%] max-w-[30%] truncate font-medium">
        {item.productName} (x{item.quantity}) (MEMBERSHIP)
      </span>
      <span className="flex-1 opacity-80">
        {item.addedDate && <>Available: {formatDate(item.addedDate)}</>}
        {item.expireDate && <> — Expires: {formatDate(item.expireDate)}</>}
      </span>
      <span className={ACTION_COL}>
        <IconBtn
          icon={<Clock className="h-3.5 w-3.5" />}
          title="Upcoming — not yet available"
          onClick={() => {}}
          className="bg-white/25 text-white cursor-default"
        />
      </span>
    </div>
  );
}

const HOLD_REASON_LABELS: Record<string, string> = {
  frozen: 'Membership Frozen',
  declined: 'Membership Past Due',
  cancelled: 'Membership Cancelled',
};

export function OnHoldRow({ item, onAction, readOnly, canDelete }: {
  item: ProductItem;
  onAction?: ActionHandler;
  readOnly?: boolean;
  canDelete?: boolean;
}) {
  const source = getPaymentSource(item);
  const label = HOLD_REASON_LABELS[item.holdReason ?? ''] ?? 'On Hold';
  return (
    <div className="flex items-center gap-3 rounded bg-amber-600 px-2 py-1.5 text-xs text-white">
      <span className="w-[30%] max-w-[30%] truncate font-medium">
        {item.productName} (x{item.quantity}) {source}
      </span>
      <span className="flex-1 opacity-80">
        {item.addedDate && <>Added: {formatDate(item.addedDate)}</>}
        {(item.originalExpireDate || item.expireDate) && <> — Expires: {formatDate(item.originalExpireDate ?? item.expireDate)}</>}
      </span>
      <span className="shrink-0 rounded bg-white/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
        {label}
      </span>
      <span className={ACTION_COL}>
        {!readOnly && onAction && canDelete ? (
          <DeleteButton item={item} onAction={onAction} />
        ) : (
          <IconBtn
            icon={<Pause className="h-3.5 w-3.5" />}
            title={label}
            onClick={() => {}}
            className="bg-white/25 text-white cursor-default"
          />
        )}
      </span>
    </div>
  );
}

export function UsedRow({ item, onAction, onOpenAppointment }: {
  item: ProductItem;
  onAction: ActionHandler;
  onOpenAppointment?: (id: number) => void;
}) {
  const source = getPaymentSource(item);
  return (
    <div className="flex items-center gap-3 rounded bg-[#748094] px-2 py-1.5 text-xs text-white">
      <span className="w-[30%] max-w-[30%] truncate font-medium">
        {item.productName} (x{item.quantity}) {source}
      </span>
      <span className="flex-1 opacity-80">Used: {formatDate(item.usedDate)}</span>
      <span className={ACTION_COL}>
        {onOpenAppointment && item.scheduledAppointmentsID && (
          <ViewAppointmentButton appointmentId={item.scheduledAppointmentsID} onOpenAppointment={onOpenAppointment} />
        )}
        <ReturnButton item={item} onAction={onAction} />
      </span>
    </div>
  );
}

// ── Used grouping ───────────────────────────────────────────────────────────

interface UsedGroup {
  key: string;
  apptId: number | null;
  apptTypeName: string | null;
  apptDate: string | null;
  usedDate: string | null;
  items: ProductItem[];
  totalQty: number;
}

function groupUsedProducts(items: ProductItem[]): { apptGroups: UsedGroup[]; manualItems: ProductItem[] } {
  const apptMap = new Map<number, UsedGroup>();
  const manual: ProductItem[] = [];

  for (const item of items) {
    if (item.scheduledAppointmentsID) {
      const existing = apptMap.get(item.scheduledAppointmentsID);
      if (existing) {
        existing.items.push(item);
        existing.totalQty += item.quantity;
        if (!existing.apptTypeName && item.appointmentTypeName) {
          existing.apptTypeName = item.appointmentTypeName;
        }
        if (!existing.apptDate && item.scheduledAppointmentStartTime) {
          existing.apptDate = item.scheduledAppointmentStartTime;
        }
        if (!existing.usedDate && item.usedDate && item.usedDate !== '0000-00-00 00:00:00') {
          existing.usedDate = item.usedDate;
        }
      } else {
        apptMap.set(item.scheduledAppointmentsID, {
          key: `appt-${item.scheduledAppointmentsID}`,
          apptId: item.scheduledAppointmentsID,
          apptTypeName: item.appointmentTypeName ?? null,
          apptDate: item.scheduledAppointmentStartTime ?? null,
          usedDate: item.usedDate && item.usedDate !== '0000-00-00 00:00:00' ? item.usedDate : null,
          items: [item],
          totalQty: item.quantity,
        });
      }
    } else {
      manual.push(item);
    }
  }

  const apptGroups = Array.from(apptMap.values()).sort((a, b) => {
    const da = a.usedDate ?? a.apptDate ?? '';
    const db = b.usedDate ?? b.apptDate ?? '';
    return db.localeCompare(da);
  });

  manual.sort((a, b) => {
    const da = a.usedDate ?? '';
    const db = b.usedDate ?? '';
    return db.localeCompare(da);
  });

  return { apptGroups, manualItems: manual };
}

function UsedApptGroupRow({ group, onAction, onOpenAppointment, onRefetch, readOnly }: {
  group: UsedGroup;
  onAction: ActionHandler;
  onOpenAppointment?: (id: number) => void;
  onRefetch?: () => Promise<unknown>;
  readOnly?: boolean;
}) {
  const [returnQty, setReturnQty] = useState(group.items.length);
  const [busy, setBusy] = useState(false);

  async function handleReturn() {
    const toReturn = group.items.slice(0, returnQty);
    if (toReturn.length === 0) return;
    const label = group.apptTypeName || `Appt #${group.apptId}`;
    if (!window.confirm(`Return ${returnQty} product${returnQty > 1 ? 's' : ''} from "${label}"?`)) return;
    setBusy(true);
    try {
      for (const item of toReturn) {
        await onAction('return', item, undefined, true);
      }
      if (onRefetch) await onRefetch();
    } finally {
      setBusy(false);
      setReturnQty(group.items.length);
    }
  }

  return (
    <div className="flex items-center gap-3 rounded bg-[#748094] px-2 py-1.5 text-xs text-white">
      <span className="shrink-0 font-semibold">(x{group.totalQty})</span>
      <span className="truncate font-medium">
        {group.apptTypeName || `Appt #${group.apptId}`}
      </span>
      <span className="shrink-0 opacity-80">
        {group.apptDate ? formatDate(group.apptDate) : ''}
      </span>
      {group.usedDate && (
        <span className="shrink-0 opacity-70">
          Used: {formatDate(group.usedDate)}
        </span>
      )}
      {(() => {
        const name = group.items[0]?.actionUserName?.trim();
        return name ? <span className="shrink-0 opacity-70">by {name}</span> : null;
      })()}
      <span className="flex-1" />
      <span className={ACTION_COL}>
        {onOpenAppointment && group.apptId && (
          <ViewAppointmentButton appointmentId={group.apptId} onOpenAppointment={onOpenAppointment} size="md" />
        )}
        {!readOnly && (
          <>
            {group.items.length > 1 && (
              <QtySelect max={group.items.length} value={returnQty} onChange={setReturnQty} />
            )}
            <IconBtn
              icon={<Undo2 className="h-3.5 w-3.5" />}
              title={`Return ${returnQty}`}
              onClick={handleReturn}
              disabled={busy}
              loading={busy}
              className="bg-amber-600 text-white hover:bg-amber-700 hover:ring-2 hover:ring-amber-300"
              size="md"
            />
          </>
        )}
      </span>
    </div>
  );
}

function UsedManualRow({ item, onAction, readOnly }: {
  item: ProductItem;
  onAction: ActionHandler;
  readOnly?: boolean;
}) {
  const userName = item.actionUserName?.trim();
  return (
    <div className="flex items-center gap-3 rounded bg-[#748094] px-2 py-1.5 text-xs text-white">
      <span className="shrink-0 font-semibold">(x{item.quantity})</span>
      <span className="font-medium">Manually Disbursed</span>
      {userName && <span className="opacity-80">by {userName}</span>}
      {item.usedDate && item.usedDate !== '0000-00-00 00:00:00' && (
        <span className="opacity-80">Used: {formatDate(item.usedDate)}</span>
      )}
      <span className="flex-1" />
      {!readOnly && (
        <span className={ACTION_COL}>
          <ReturnButton item={item} onAction={onAction} />
        </span>
      )}
    </div>
  );
}

export function ExpiredDeletedRow({ item, onAction, readOnly, onUpdateExpireDate, canEditExpireDate }: {
  item: ProductItem;
  onAction: ActionHandler;
  readOnly?: boolean;
  onUpdateExpireDate?: UpdateExpireDateHandler;
  canEditExpireDate?: boolean;
}) {
  const source = getPaymentSource(item);
  const isDeleted = item.deleteDate && item.deleteDate !== '0000-00-00 00:00:00';
  const isExpired = !isDeleted && isExpiredAndNotDeleted(item);
  const label = isDeleted ? 'Deleted' : 'Expired';
  const hasHoldExpire = !isDeleted && item.effectiveExpireDate && item.holdReason;
  const date = isDeleted ? item.deleteDate : (hasHoldExpire ? item.effectiveExpireDate : item.expireDate);
  const canReturn = isDeleted || !isExpired;
  const holdReasonLabel = hasHoldExpire ? (HOLD_REASON_LABELS[item.holdReason!] ?? '') : '';

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [qty, setQty] = useState(item.quantity);
  const todayStr = new Date().toISOString().slice(0, 10);
  const minDate = todayStr;
  const itemExpireStr = item.expireDate?.slice(0, 10) ?? '';
  const defaultDate = itemExpireStr >= todayStr ? itemExpireStr : todayStr;
  const [newDate, setNewDate] = useState(defaultDate);

  const handleSave = async () => {
    if (!onUpdateExpireDate || !newDate || newDate < todayStr) return;
    setSaving(true);
    try {
      await onUpdateExpireDate(item, newDate, qty);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 rounded bg-[#748094] px-2 py-1.5 text-xs text-white">
        <span className="w-[30%] max-w-[30%] truncate font-medium">
          {item.productName} (x{item.quantity}) {source}
        </span>
        <span className="flex-1 opacity-80">
          {label}: {formatDate(date)}
          {holdReasonLabel && (
            <span className="ml-1.5 rounded bg-white/20 px-1 py-0.5 text-[10px] font-semibold uppercase" title={`Original expiration: ${formatDate(item.expireDate)}`}>
              {holdReasonLabel}
            </span>
          )}
        </span>
        {!readOnly && (
          <span className={ACTION_COL}>
            {isDeleted ? (
              <ReturnButton item={item} onAction={onAction} />
            ) : isExpired && canEditExpireDate && onUpdateExpireDate ? (
              <IconBtn
                icon={<CalendarClock className="h-3 w-3" />}
                title="Edit expiration date"
                onClick={() => setEditing(!editing)}
                className="bg-white/20 text-white hover:bg-white/30"
                size="md"
              />
            ) : canReturn ? null : (
              <IconBtn
                icon={<Circle className="h-3 w-3 fill-white/60" />}
                title="Cannot return expired products"
                onClick={() => {}}
                className="bg-white/15 text-white/60 cursor-not-allowed"
              />
            )}
          </span>
        )}
      </div>
      {editing && (
        <div className="flex items-center gap-2 rounded-b border border-t-0 border-gray-200 bg-gray-50 px-3 py-2">
          {item.quantity > 1 && (
            <>
              <label className="text-xs font-medium text-gray-600">Qty:</label>
              <select
                value={qty}
                onChange={(e) => setQty(Number(e.target.value))}
                className="h-6 rounded border border-gray-300 bg-white px-1 text-xs text-gray-700"
              >
                {Array.from({ length: item.quantity }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <span className="text-xs text-gray-400">of {item.quantity}</span>
            </>
          )}
          <label className="text-xs font-medium text-gray-600">New expiration:</label>
          <DatePicker
            size="sm"
            min={minDate}
            value={newDate}
            onChange={setNewDate}
            ariaLabel="New expiration date"
            wrapperClassName="w-40"
          />
          <button
            type="button"
            disabled={saving || !newDate || newDate < todayStr}
            onClick={handleSave}
            className="inline-flex items-center gap-1 rounded bg-blue-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-600 disabled:opacity-50 transition-colors"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            Save
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <X className="h-3 w-3" />
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// ── Lifetime Stats ──────────────────────────────────────────────────────────

function LifetimeStatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-center">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm font-bold tabular-nums text-slate-900">{value}</p>
    </div>
  );
}

function LifetimeStats({ group, onDetailsClick }: {
  group: ProductGroup;
  onDetailsClick: () => void;
}) {
  const unavailableArr = toArray<ProductItem>(group.unavailable);

  const upcomingInAppts = toArray<ProductItem>(group.appointments)
    .filter((p) => !resolveTypeAndId(p))
    .reduce((sum, p) => sum + p.quantity, 0);

  const totalReceived =
    group.availableQuantity +
    (group.appointmentsQuantity - upcomingInAppts) +
    group.usedQuantity +
    group.expireQuantity +
    group.deleteQuantity;

  const allocatedNonUpcoming = group.appointmentsQuantity - upcomingInAppts;

  const usedOnAppts = allocatedNonUpcoming + unavailableArr
    .filter((p) => p.usedDate && p.usedDate !== '0000-00-00 00:00:00' && p.scheduledAppointmentsID)
    .reduce((sum, p) => sum + p.quantity, 0);

  const disbursedManual = unavailableArr
    .filter((p) => p.usedDate && p.usedDate !== '0000-00-00 00:00:00' && !p.scheduledAppointmentsID)
    .reduce((sum, p) => sum + p.quantity, 0);

  const compedCount = group.compedQuantity ?? 0;

  const expiredDeleted = group.expireQuantity + group.deleteQuantity;
  const totalConsumed = usedOnAppts + disbursedManual + expiredDeleted;

  return (
    <div className="mt-3 border-t border-dashed border-slate-200 pt-3">
      <div className="grid grid-cols-5 gap-2">
        <LifetimeStatCard label="Total Received" value={totalReceived} />
        <LifetimeStatCard label="Total Used" value={usedOnAppts + disbursedManual} />
        <LifetimeStatCard label="Comped" value={compedCount} />
        <LifetimeStatCard label="Expired / Deleted" value={expiredDeleted} />
        <LifetimeStatCard label="Total Consumed" value={totalConsumed} />
      </div>
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={onDetailsClick}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50"
        >
          <BarChart3 className="h-3 w-3" />
          Details
        </button>
      </div>
    </div>
  );
}

// ── Product Group Card ───────────────────────────────────────────────────────

export function ProductGroupCard({ group, listType, onAction, onBatchConsume, onOpenAppointment, onRefetch, readOnly, headerExtra, onUpdateExpireDate, canEditExpireDate, canDeleteProducts }: {
  group: ProductGroup;
  listType: string;
  onAction: ActionHandler;
  onBatchConsume?: BatchConsumeHandler;
  onRefetch?: () => Promise<unknown>;
  onOpenAppointment?: (id: number) => void;
  readOnly?: boolean;
  headerExtra?: React.ReactNode;
  onUpdateExpireDate?: UpdateExpireDateHandler;
  canEditExpireDate?: boolean;
  /**
   * Permission 19 ("Delete Products"), or user level 4+. Gates the Delete
   * button on available and on-hold rows only — Use and Return stay open.
   * Absent means no delete, so every read-only surface stays read-only.
   */
  canDeleteProducts?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const name = listType === 'product-others'
    ? (group.productName || group.productTypeName || 'Unknown Product')
    : (group.productTypeName || 'Unknown Type');

  const appointmentItems = useMemo(() => toArray<ProductItem>(group.appointments), [group.appointments]);

  const appointmentGroups = useMemo(
    () => groupByAppointment(appointmentItems),
    [appointmentItems],
  );

  const upcomingApptGroups = useMemo(
    () => appointmentGroups.filter((ag) => ag.allUpcoming),
    [appointmentGroups],
  );

  const upcomingApptQty = useMemo(
    () => upcomingApptGroups.reduce((s, ag) => s + ag.totalQty, 0),
    [upcomingApptGroups],
  );

  const totalUpcomingCount = group.upcomingQuantity + upcomingApptQty;

  const compedItems = useMemo(() => toArray<ProductItem>(group.comped), [group.comped]);

  const compApptGroups = useMemo(
    () => groupByAppointment(compedItems).sort((a, b) => {
      const da = a.date ?? '';
      const db = b.date ?? '';
      return db.localeCompare(da);
    }),
    [compedItems],
  );

  const purchaseGroups = useMemo(() => {
    const purchaseArr = toArray<ProductItem>(group.purchase);
    if (purchaseArr.length === 0) return [];
    const byAppt = new Map<string, { apptId: number | null; date: string | null; totalQty: number; apptTypeName: string | null }>();
    for (const p of purchaseArr) {
      const key = String(p.scheduledAppointmentsID ?? 'none');
      const existing = byAppt.get(key);
      if (existing) {
        existing.totalQty += p.quantity;
      } else {
        byAppt.set(key, {
          apptId: p.scheduledAppointmentsID,
          date: p.scheduledAppointmentStartTime ?? null,
          totalQty: p.quantity,
          apptTypeName: p.appointmentTypeName ?? null,
        });
      }
    }
    return Array.from(byAppt.values());
  }, [group.purchase]);

  const purchaseTotal = useMemo(
    () => toArray<ProductItem>(group.purchase).reduce((s, p) => s + p.quantity, 0),
    [group.purchase],
  );

  const expiringCount = useMemo(() => {
    const now = new Date();
    const twoWeeks = new Date(now);
    twoWeeks.setDate(twoWeeks.getDate() + 14);
    let count = 0;
    for (const item of toArray<ProductItem>(group.available)) {
      if (!item.expireDate || item.expireDate === '0000-00-00 00:00:00') continue;
      if (item.scheduledAppointmentsID) continue;
      const expires = new Date(item.expireDate);
      if (expires > now && expires <= twoWeeks) count += item.quantity;
    }
    return count;
  }, [group.available]);

  const unavailableArr = toArray<ProductItem>(group.unavailable);
  const usedProducts = unavailableArr
    .filter((p) => p.usedDate && p.usedDate !== '0000-00-00 00:00:00')
    .sort((a, b) => (b.usedDate ?? '').localeCompare(a.usedDate ?? ''));
  const expiredDeletedProducts = unavailableArr
    .filter((p) => !p.usedDate || p.usedDate === '0000-00-00 00:00:00')
    .sort((a, b) => {
      const aDate = (a.deleteDate && a.deleteDate !== '0000-00-00 00:00:00' ? a.deleteDate : a.expireDate) ?? '';
      const bDate = (b.deleteDate && b.deleteDate !== '0000-00-00 00:00:00' ? b.deleteDate : b.expireDate) ?? '';
      return bDate.localeCompare(aDate);
    });

  const { apptGroups: usedApptGroups, manualItems: usedManualItems } = useMemo(
    () => groupUsedProducts(usedProducts),
    [usedProducts],
  );

  return (
    <div className="mb-2 rounded border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50"
      >
        <span className={`text-xs text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}>
          &#9654;
        </span>
        <span className="flex-1 text-sm font-semibold text-gray-800">
          {name}
          {!readOnly && purchaseTotal > 0 && (
            <span className="ml-2 text-xs font-normal text-green-600">Purchase Required</span>
          )}
          {headerExtra && (
            <span className="ml-2 text-xs font-normal">{headerExtra}</span>
          )}
        </span>
        <div className="flex items-center gap-1">
          {expiringCount > 0 && (
            <CountBadge count={expiringCount} icon={<AlertTriangle className="h-2.5 w-2.5" />} title="Expiring Soon" bg="bg-amber-100" text="text-amber-800" />
          )}
          {!readOnly && purchaseTotal > 0 && (
            <CountBadge count={purchaseTotal} icon={<DollarSign className="h-2.5 w-2.5" />} title="Purchase Required" bg="bg-green-100" text="text-green-700" />
          )}
          {totalUpcomingCount > 0 && (
            <CountBadge count={totalUpcomingCount} icon={<Clock className="h-2.5 w-2.5" />} title="Upcoming" bg="bg-orange-100" text="text-orange-800" />
          )}
          <CountBadge count={group.availableQuantity} icon={<ArrowRight className="h-3 w-3" />} title="Available" bg="bg-blue-100" text="text-blue-800" large />
        </div>
      </button>

      {open && (
        <div className="border-t border-gray-100 px-3 py-2">
          {!readOnly && listType !== 'product-others' && <AutoDisburseNotice />}
          {listType !== 'product-others' && (
            <SubListToggle title="Appointments" count={group.appointmentsQuantity} countColor="text-[#357bb8]">
              {appointmentGroups.map((ag, i) => (
                <AppointmentGroupRow
                  key={i}
                  group={ag}
                  onBatchConsume={readOnly ? undefined : onBatchConsume}
                  onOpenAppointment={onOpenAppointment}
                  readOnly={readOnly}
                />
              ))}
            </SubListToggle>
          )}

          {!readOnly && purchaseGroups.length > 0 && (
            <SubListToggle title="Purchase Required" count={purchaseTotal} countColor="text-green-700" defaultOpen>
              {purchaseGroups.map((g, i) => (
                <div key={i} className="flex items-center gap-3 rounded bg-green-50 px-2 py-1.5 text-xs">
                  <span className="font-medium text-green-800">
                    (x{g.totalQty}) needed
                  </span>
                  {g.apptTypeName && (
                    <span className="rounded bg-green-100 px-1.5 py-0.5 font-medium text-green-900">
                      {g.apptTypeName}
                    </span>
                  )}
                  <span className="text-green-700">
                    {g.date ? <>Appt: {formatDate(g.date)}</> : 'Unscheduled'}
                  </span>
                  <span className="flex-1" />
                  {onOpenAppointment && g.apptId && (
                    <ViewAppointmentButton appointmentId={g.apptId} onOpenAppointment={onOpenAppointment} />
                  )}
                </div>
              ))}
            </SubListToggle>
          )}

          {(group.onHoldQuantity ?? 0) > 0 && (
            <SubListToggle title="On Hold" count={group.onHoldQuantity} countColor="text-amber-600">
              {toArray<ProductItem>(group.onHold).map((item, i) => (
                <OnHoldRow key={i} item={item} onAction={onAction} readOnly={readOnly} canDelete={canDeleteProducts} />
              ))}
            </SubListToggle>
          )}

          <SubListToggle title="Available Products" count={group.availableQuantity} countColor="text-[#2f4d7e]">
            {toArray<ProductItem>(group.available).map((item, i) => (
              <AvailableRow key={i} item={item} onAction={onAction} readOnly={readOnly} onUpdateExpireDate={onUpdateExpireDate} canEditExpireDate={canEditExpireDate} canDelete={canDeleteProducts} />
            ))}
          </SubListToggle>

          <SubListToggle title="Upcoming Membership Products" count={totalUpcomingCount} countColor="text-[#c97a28]">
            {toArray<ProductItem>(group.upcoming).map((item, i) => (
              <UpcomingRow key={i} item={item} />
            ))}
            {upcomingApptGroups.map((ag, i) => (
              <AppointmentGroupRow
                key={`upcoming-appt-${i}`}
                group={ag}
                onOpenAppointment={onOpenAppointment}
                readOnly
              />
            ))}
          </SubListToggle>

          <SubListToggle title="Used Products" count={group.usedQuantity} countColor="text-[#748094]">
            <div className="max-h-48 space-y-1 overflow-y-auto">
              {usedApptGroups.map((g) => (
                <UsedApptGroupRow
                  key={g.key}
                  group={g}
                  onAction={onAction}
                  onOpenAppointment={onOpenAppointment}
                  onRefetch={onRefetch}
                  readOnly={readOnly}
                />
              ))}
              {usedManualItems.map((item, i) => (
                <UsedManualRow key={`manual-${i}`} item={item} onAction={onAction} readOnly={readOnly} />
              ))}
            </div>
          </SubListToggle>

          {!readOnly && (
            <SubListToggle title="Comped Products" count={group.compedQuantity ?? 0} countColor="text-[#6b7a8d]">
              <div className="max-h-48 space-y-1 overflow-y-auto">
                {compApptGroups.map((ag, i) => (
                  <AppointmentGroupRow
                    key={`comp-${i}`}
                    group={ag}
                    onOpenAppointment={onOpenAppointment}
                    colorClass="bg-[#6b7a8d]"
                    isComped
                  />
                ))}
              </div>
            </SubListToggle>
          )}

          <SubListToggle
            title="Expired and Deleted Products"
            count={group.expireQuantity + group.deleteQuantity}
            countColor="text-[#748094]"
          >
            <div className="max-h-48 overflow-y-auto">
              {expiredDeletedProducts.map((item, i) => (
                <ExpiredDeletedRow key={i} item={item} onAction={onAction} readOnly={readOnly} onUpdateExpireDate={onUpdateExpireDate} canEditExpireDate={canEditExpireDate} />
              ))}
            </div>
          </SubListToggle>

          {!readOnly && <LifetimeStats group={group} onDetailsClick={() => setHistoryOpen(true)} />}
        </div>
      )}

      {!readOnly && (
        <PatientProductHistoryModal
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
          group={group}
          productName={name}
          onOpenAppointment={onOpenAppointment}
        />
      )}
    </div>
  );
}

// ── Expiring Products Alert ──────────────────────────────────────────────────

interface ExpiringEntry {
  name: string;
  quantity: number;
  expireDate: string;
}

function getExpiringProducts(sortedGroups: { group: ProductGroup }[]): ExpiringEntry[] {
  const now = new Date();
  const twoWeeks = new Date(now);
  twoWeeks.setDate(twoWeeks.getDate() + 14);

  const entries: ExpiringEntry[] = [];

  for (const { group } of sortedGroups) {
    const available = toArray<ProductItem>(group.available);
    const byExpire = new Map<string, ExpiringEntry>();

    for (const item of available) {
      if (!item.expireDate || item.expireDate === '0000-00-00 00:00:00') continue;
      if (item.scheduledAppointmentsID) continue;

      const expires = new Date(item.expireDate);
      if (expires <= now || expires > twoWeeks) continue;

      const key = `${item.productTypeName || item.productName}|${item.expireDate}`;
      const existing = byExpire.get(key);
      if (existing) {
        existing.quantity += item.quantity;
      } else {
        byExpire.set(key, {
          name: item.productTypeName || item.productName,
          quantity: item.quantity,
          expireDate: item.expireDate,
        });
      }
    }

    entries.push(...byExpire.values());
  }

  entries.sort((a, b) => new Date(a.expireDate).getTime() - new Date(b.expireDate).getTime());
  return entries;
}

function ExpiringProductsAlert({ entries }: { entries: ExpiringEntry[] }) {
  if (entries.length === 0) return null;
  return (
    <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
      <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-amber-800">
        <AlertTriangle className="h-3.5 w-3.5" />
        Expiring Soon
      </div>
      <div className="space-y-0.5">
        {entries.map((e, i) => (
          <div key={i} className="flex items-center gap-2 text-xs text-amber-700">
            <span className="font-medium">{e.quantity}x {e.name}</span>
            <span className="opacity-70">expires {formatDate(e.expireDate)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Purchase Required Alert ──────────────────────────────────────────────────

interface PurchaseLineItem {
  name: string;
  quantity: number;
  rate: number;
}

interface PurchaseSummary {
  lines: PurchaseLineItem[];
  totalQty: number;
  totalCost: number;
  isMember: boolean;
  appointmentCount: number;
}

function getPurchaseSummary(
  sortedGroups: { group: ProductGroup }[],
  isMember: boolean,
): PurchaseSummary {
  const byType = new Map<string, PurchaseLineItem>();
  const apptIds = new Set<number>();

  for (const { group } of sortedGroups) {
    const purchaseArr = toArray<ProductItem>(group.purchase);
    if (purchaseArr.length === 0) continue;

    const rate = group.curRate ?? 0;
    const typeName = group.productTypeName || group.productName || 'Unknown';

    for (const item of purchaseArr) {
      if (item.scheduledAppointmentsID) apptIds.add(item.scheduledAppointmentsID);
      const existing = byType.get(typeName);
      if (existing) {
        existing.quantity += item.quantity;
      } else {
        byType.set(typeName, { name: typeName, quantity: item.quantity, rate });
      }
    }
  }

  const lines = Array.from(byType.values()).sort((a, b) => a.name.localeCompare(b.name));
  const totalQty = lines.reduce((s, l) => s + l.quantity, 0);
  const totalCost = lines.reduce((s, l) => s + l.quantity * l.rate, 0);

  return { lines, totalQty, totalCost, isMember, appointmentCount: apptIds.size };
}

function PurchaseRequiredAlert({ summary, onPurchase }: {
  summary: PurchaseSummary;
  onPurchase?: () => void;
}) {
  if (summary.lines.length === 0) return null;
  const apptWord = summary.appointmentCount === 1 ? 'appointment' : 'appointments';
  return (
    <div className="mb-3 rounded-lg border border-green-300 bg-white px-3 py-2">
      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-green-800">
        <DollarSign className="h-3.5 w-3.5" />
        Purchase Required
        {summary.isMember && (
          <span className="rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
            Member Pricing
          </span>
        )}
      </div>
      <div className="space-y-1">
        {summary.lines.map((l, i) => (
          <div key={i} className="flex items-center justify-between text-xs text-gray-700">
            <span className="font-medium">{l.quantity}x {l.name}</span>
            {l.rate > 0 && (
              <span className="tabular-nums text-gray-500">${(l.quantity * l.rate).toFixed(2)}</span>
            )}
          </div>
        ))}
      </div>
      <div className="mt-1.5 flex items-start justify-between gap-3 border-t border-green-200 pt-1.5">
        <p className="min-w-0 max-w-xs text-xs text-gray-500">
          Products must be purchased for {summary.appointmentCount} {apptWord} - the patient does not have the required products on the scheduled dates.
        </p>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {summary.totalCost > 0 && (
            <span className="text-xs font-bold tabular-nums text-gray-900">${summary.totalCost.toFixed(2)}</span>
          )}
          {onPurchase && (
            <button
              type="button"
              onClick={onPurchase}
              className="inline-flex items-center gap-1.5 rounded bg-blue-500 px-4 py-1.5 text-xs font-medium text-white hover:bg-blue-600"
            >
              <ShoppingCart className="h-3.5 w-3.5" />
              Purchase
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Display ─────────────────────────────────────────────────────────────

export function ProductTypesDisplay({ data, onAction, onBatchConsume, onOpenAppointment, onPurchase, onRefetch, onUpdateExpireDate, canEditExpireDate, canDeleteProducts }: {
  data: ProductTypesResponse;
  onAction: ActionHandler;
  onBatchConsume?: BatchConsumeHandler;
  onOpenAppointment?: (appointmentId: number) => void;
  onPurchase?: () => void;
  onRefetch?: () => Promise<unknown>;
  onUpdateExpireDate?: UpdateExpireDateHandler;
  canEditExpireDate?: boolean;
  canDeleteProducts?: boolean;
}) {
  const productData = data?.data;

  const sortedGroups = useMemo(() => {
    if (!productData) return [];

    const combined: { group: ProductGroup; listType: string; sortName: string }[] = [];

    const types = productData['product-types'] ?? {};
    for (const key of Object.keys(types)) {
      const g = types[key];
      combined.push({
        group: g,
        listType: 'product-types',
        sortName: (g.productTypeName ?? '').toLowerCase(),
      });
    }

    const others = productData['product-others'] ?? {};
    for (const key of Object.keys(others)) {
      const g = others[key];
      combined.push({
        group: g,
        listType: 'product-others',
        sortName: (g.productName ?? g.productTypeName ?? '').toLowerCase(),
      });
    }

    combined.sort((a, b) => a.sortName.localeCompare(b.sortName));
    return combined;
  }, [productData]);

  const hasUsedOnly = useMemo(() => {
    return sortedGroups.some(({ group: g }) => {
      const active = g.availableQuantity + g.upcomingQuantity + g.appointmentsQuantity + toArray(g.purchase).length;
      const inactive = g.usedQuantity + g.expireQuantity + g.deleteQuantity;
      return active === 0 && inactive > 0;
    });
  }, [sortedGroups]);

  const expiringEntries = useMemo(
    () => getExpiringProducts(sortedGroups),
    [sortedGroups],
  );

  const purchaseSummary = useMemo(
    () => getPurchaseSummary(sortedGroups, productData?.isMember ?? false),
    [sortedGroups, productData],
  );

  const [showUsedOnly, setShowUsedOnly] = useState(false);

  const activeGroups = sortedGroups.filter(({ group: g }) => {
    return g.availableQuantity + g.upcomingQuantity + g.appointmentsQuantity + toArray(g.purchase).length > 0;
  });

  const usedOnlyGroups = sortedGroups.filter(({ group: g }) => {
    const active = g.availableQuantity + g.upcomingQuantity + g.appointmentsQuantity + toArray(g.purchase).length;
    const inactive = g.usedQuantity + g.expireQuantity + g.deleteQuantity;
    return active === 0 && inactive > 0;
  });

  if (sortedGroups.length === 0) {
    return <p className="py-4 text-sm text-gray-500">No products found for this patient.</p>;
  }

  return (
    <div className="space-y-1">
      <ExpiringProductsAlert entries={expiringEntries} />
      <PurchaseRequiredAlert summary={purchaseSummary} onPurchase={onPurchase} />
      {activeGroups.map(({ group, listType }) => (
        <ProductGroupCard
          key={`active-${listType}-${group.productTypeID ?? group.productID ?? group.productTypeName}`}
          group={group}
          listType={listType}
          onAction={onAction}
          onBatchConsume={onBatchConsume}
          onOpenAppointment={onOpenAppointment}
          onRefetch={onRefetch}
          onUpdateExpireDate={onUpdateExpireDate}
          canEditExpireDate={canEditExpireDate}
          canDeleteProducts={canDeleteProducts}
        />
      ))}

      {hasUsedOnly && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setShowUsedOnly(!showUsedOnly)}
            className="text-xs font-semibold text-blue-400 hover:text-blue-600"
          >
            {showUsedOnly ? 'Hide' : 'Show'} More Used Products
          </button>
          {showUsedOnly && (
            <div className="mt-2 space-y-1">
              {usedOnlyGroups.map(({ group, listType }) => (
                <ProductGroupCard
                  key={`used-${listType}-${group.productTypeID ?? group.productID ?? group.productTypeName}`}
                  group={group}
                  listType={listType}
                  onAction={onAction}
                  onBatchConsume={onBatchConsume}
                  onOpenAppointment={onOpenAppointment}
                  onRefetch={onRefetch}
                  onUpdateExpireDate={onUpdateExpireDate}
                  canEditExpireDate={canEditExpireDate}
                  canDeleteProducts={canDeleteProducts}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
